import React from 'react';
import {
  Package, UtensilsCrossed, Users, DollarSign, Clock, CheckCircle, ChefHat, Truck,
  TrendingUp, Plus, Edit, Trash2, GripVertical, ArrowUp, ArrowDown, FolderOpen,
  Upload, Image, Star, MessageSquare, Mail, Eye, Reply, Send, Megaphone, Tag, Calendar, X, ChevronLeft
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { TabsContent } from '../ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { apiRequest, formatPrice, getAuthToken } from '../../lib/utils';

const ReviewsTab = ({
  deleteReview,
  replyText,
  replyToReview,
  replyingTo,
  reviews,
  setReplyText,
  setReplyingTo,
}) => (
          <TabsContent value="reviews">
            <div className="space-y-6">
              {/* Reviews Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="bg-[#141417] border-[#27272A]">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-zinc-400">Total Reviews</CardTitle>
                    <MessageSquare className="h-5 w-5 text-blue-500" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{reviews.total_reviews}</div>
                  </CardContent>
                </Card>

                <Card className="bg-[#141417] border-[#27272A]">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-zinc-400">Average Rating</CardTitle>
                    <Star className="h-5 w-5 text-[#FFC107]" />
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-2">
                      <span className="text-2xl font-bold text-[#FFC107]">{reviews.average_rating}</span>
                      <div className="flex">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`h-4 w-4 ${
                              star <= Math.round(reviews.average_rating)
                                ? 'text-[#FFC107] fill-[#FFC107]'
                                : 'text-zinc-600'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-[#141417] border-[#27272A]">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-zinc-400">Rating Distribution</CardTitle>
                    <TrendingUp className="h-5 w-5 text-green-500" />
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-1 text-sm">
                      {[5, 4, 3, 2, 1].map((rating) => {
                        const count = reviews.reviews?.filter(r => r.rating === rating).length || 0;
                        const percentage = reviews.total_reviews > 0 ? (count / reviews.total_reviews) * 100 : 0;
                        return (
                          <div key={rating} className="flex items-center gap-2">
                            <span className="w-3">{rating}</span>
                            <Star className="h-3 w-3 text-[#FFC107] fill-[#FFC107]" />
                            <div className="flex-1 h-2 bg-[#1C1C22] rounded-full overflow-hidden">
                              <div
                                className="h-full bg-[#FFC107]"
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                            <span className="text-xs text-zinc-500 w-8">{count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Reviews List */}
              <Card className="bg-[#141417] border-[#27272A]">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5 text-[#FFC107]" />
                    Customer Reviews
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {reviews.reviews?.length === 0 ? (
                    <div className="text-center py-12">
                      <MessageSquare className="h-12 w-12 text-zinc-700 mx-auto mb-4" />
                      <p className="text-zinc-500">No reviews yet</p>
                      <p className="text-zinc-600 text-sm">Customer reviews will appear here</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {reviews.reviews?.map((review) => (
                        <div
                          key={review.id}
                          className="bg-[#0B0B0C] rounded-lg p-4 border border-[#27272A]"
                          data-testid={`review-${review.id}`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h4 className="font-semibold text-white">{review.name}</h4>
                              <p className="text-sm text-zinc-500">{review.email}</p>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="flex items-center gap-1">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <Star
                                    key={star}
                                    className={`h-4 w-4 ${
                                      star <= review.rating
                                        ? 'text-[#FFC107] fill-[#FFC107]'
                                        : 'text-zinc-600'
                                    }`}
                                  />
                                ))}
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 w-8"
                                onClick={() => deleteReview(review.id)}
                                data-testid={`delete-review-${review.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                          {review.feedback && (
                            <p className="text-zinc-300 text-sm mt-2">{review.feedback}</p>
                          )}
                          
                          {/* Admin Reply Section */}
                          {review.admin_reply ? (
                            <div className="mt-3 p-3 bg-[#FFC107]/10 rounded-lg border border-[#FFC107]/20">
                              <div className="flex items-center gap-2 mb-1">
                                <Reply className="h-4 w-4 text-[#FFC107]" />
                                <span className="text-xs font-semibold text-[#FFC107]">Owner's Reply</span>
                              </div>
                              <p className="text-zinc-300 text-sm">{review.admin_reply}</p>
                            </div>
                          ) : replyingTo === review.id ? (
                            <div className="mt-3 space-y-2">
                              <Textarea
                                value={replyText}
                                onChange={(e) => setReplyText(e.target.value)}
                                placeholder="Write your reply..."
                                className="bg-[#141417] border-zinc-700 text-sm"
                                rows={2}
                              />
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  className="bg-[#FFC107] text-black hover:bg-[#F59E0B]"
                                  onClick={() => replyToReview(review.id)}
                                >
                                  <Send className="h-3 w-3 mr-1" />
                                  Send Reply
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-zinc-700"
                                  onClick={() => { setReplyingTo(null); setReplyText(''); }}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              className="mt-3 border-zinc-700 hover:bg-[#25252E]"
                              onClick={() => setReplyingTo(review.id)}
                            >
                              <Reply className="h-3 w-3 mr-1" />
                              Reply
                            </Button>
                          )}
                          
                          <p className="text-xs text-zinc-600 mt-3">
                            {new Date(review.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
);

export default ReviewsTab;
